/**
 * AI 도우미에 "지금 화면에 쓰고 있는 내용" 을 함께 보내기 위한 등록소.
 *
 * 화면은 setup 에서 `useAssistantPageContext(() => 요약문)` 을 부르면 마운트 동안 등록되고 언마운트 시 해제된다.
 * AiAssistant 는 질문을 보내는 순간 `getAssistantPageContext()` 로 최신 요약문을 읽는다(미리 계산하지 않는다).
 * 지원자 개인정보가 있는 화면은 등록하지 않는다 — 서버 프롬프트로 그대로 넘어간다.
 */
import { onBeforeUnmount, onMounted, ref } from "vue";

/** 서버 `pageContext` 상한(8000자)과 같다. 넘치면 뒤를 자른다. */
export const ASSISTANT_PAGE_CONTEXT_MAX = 8000;

type ContextGetter = () => string | null;

/** 마지막으로 등록한 화면의 getter. 화면 전환 중 두 화면이 겹치면 나중에 마운트된 쪽이 이긴다. */
const current = ref<ContextGetter | null>(null);

export function useAssistantPageContext(getter: ContextGetter): void {
  onMounted(() => {
    current.value = getter;
  });
  onBeforeUnmount(() => {
    if (current.value === getter) current.value = null;
  });
}

/**
 * 등록된 화면이 있으면 그 화면의 요약문(공백만이면 null)을 상한까지 잘라 돌려준다.
 * 서버 상한은 글자(코드 포인트) 수라 UTF-16 단위가 아니라 코드 포인트 단위로 자른다(이모지 반쪽 방지).
 */
export function getAssistantPageContext(): string | null {
  const text = current.value?.()?.trim();
  return text
    ? Array.from(text).slice(0, ASSISTANT_PAGE_CONTEXT_MAX).join("")
    : null;
}

/**
 * 패널의 "화면 내용 함께 보냄" 표시용 — 지금 보낼 내용이 실제로 있는지.
 * 등록만 보지 않고 getter 를 불러 본다(목업 · 불러오기 전 상태의 화면은 null 을 돌려주므로 표시도 숨는다).
 * getter 가 반응형 값을 읽으므로 computed 로 감싸면 화면 상태를 따라 갱신된다.
 */
export function hasAssistantPageContext(): boolean {
  return getAssistantPageContext() !== null;
}

/** 도우미 열기 요청. `seq` 는 같은 초안을 두 번 보내도 다시 열리게 하는 순번이다. */
export interface AssistantOpenRequest {
  draft: string;
  seq: number;
}

const openRequest = ref<AssistantOpenRequest | null>(null);
let openSeq = 0;

/**
 * 도우미를 열고 입력칸에 질문 초안을 채운다(보내지는 않는다 — 사용자가 고쳐 보낸다).
 * AI 검토 결과의 「도우미에게 이어 묻기」(AiReviewPanel `askAssistant`)처럼 화면이 도우미로 대화를 넘길 때 쓴다(UXD-28).
 */
export function openAssistant(draft = ""): void {
  openSeq += 1;
  openRequest.value = { draft, seq: openSeq };
}

/** AiAssistant 가 지켜보는 마지막 열기 요청(읽기 전용). */
export function assistantOpenRequest(): AssistantOpenRequest | null {
  return openRequest.value;
}
